import { Matrix, Quaternion, TransformNode, Vector3 } from "@babylonjs/core";
import { MELEE } from "@tiny-seas/shared";
import type { ModelInstance } from "./models.ts";

export const ARM_UPPER_LENGTH = 0.34;
export const ARM_LOWER_LENGTH = 0.30;
export const LEG_UPPER_LENGTH = 0.37;
export const LEG_LOWER_LENGTH = 0.37;
export const MAX_SHOULDER_REACH = 0.12;
export const DIG_SWING_SECONDS = 1 / 3;
export const HAND_GRIP = new Vector3(0, -0.026, 0.075);
export const HANGING_HAND_GRIP = new Vector3(0, -0.075, -0.026);
/** Blade follows the right thumb; the knuckle bow must face the fingers, not the palm. */
export const SWORD_GRIP_ROTATION = Quaternion.RotationYawPitchRoll(0, 0, Math.PI / 2)
  .multiply(Quaternion.RotationYawPitchRoll(Math.PI, 0, 0));

/**
 * Handedness contract. `hands.py` builds the left hand as the exact x-mirror of the right
 * (digits at `side·x`, unrotated pivots), in both the near and full-body models. The same
 * rotation therefore gives both hands the same palm direction, not mirror-image hands.
 * Author every two-handed pose for the RIGHT hand; the left hand uses its mirror image
 * M·R·M with M = diag(−1, 1, 1), in any frame symmetric about its x = 0 plane (body, camera,
 * wheel, capstan, chest, spyglass). Positions mirror as x → −x.
 */
export function mirrorHandRotationInPlace(rotation: Quaternion): Quaternion {
  rotation.y = -rotation.y;
  rotation.z = -rotation.z;
  return rotation;
}

/** Right-hand-authored yaw/pitch/roll, mirrored when `side` is the left hand. */
export function handRotationToRef(side: number, yaw: number, pitch: number, roll: number, out: Quaternion): Quaternion {
  Quaternion.RotationYawPitchRollToRef(yaw, pitch, roll, out);
  return side < 0 ? mirrorHandRotationInPlace(out) : out;
}

interface SpyglassHandFrame { offset: Vector3; rotation: Quaternion }

/** Right-hand grip frame (eye frame: palm −y, fingers +z): palm on the surface at `azimuth`, facing the axis. */
function spyglassHandFrame(radius: number, azimuth: number, fingers: Vector3): SpyglassHandFrame {
  // Spyglass local: +x right, +y up, +z from the eyepiece (0 m) to the objective (0.32 m).
  const outward = new Vector3(Math.cos(azimuth), Math.sin(azimuth), 0);
  const along = fingers.subtract(outward.scale(Vector3.Dot(fingers, outward))).normalize();
  const rotation = Quaternion.FromRotationMatrix(Matrix.FromXYZAxesToRef(Vector3.Cross(outward, along), outward, along, new Matrix()));
  // Palm-grip centre 5 mm proud of the authored surface, as for the other barrel tools.
  return { offset: outward.scale(radius + 0.005), rotation };
}
/** Grip on the tube's upper side: fingers wrap over the top, the forearm rises from below and outboard. */
const SPYGLASS_GRIP_AZIMUTH = 0.58, SPYGLASS_GRIP_FINGERS = new Vector3(-0.55, 0.83, 0.15);
/** Right palm on the leather barrel's upper right. */
const SPYGLASS_RIGHT = spyglassHandFrame(0.031, SPYGLASS_GRIP_AZIMUTH, SPYGLASS_GRIP_FINGERS);
/** Left palm on the objective's upper left: the exact mirror image of the same grip. */
const SPYGLASS_LEFT_AS_RIGHT = spyglassHandFrame(0.035, SPYGLASS_GRIP_AZIMUTH, SPYGLASS_GRIP_FINGERS);
const SPYGLASS_LEFT: SpyglassHandFrame = {
  offset: new Vector3(-SPYGLASS_LEFT_AS_RIGHT.offset.x, SPYGLASS_LEFT_AS_RIGHT.offset.y, SPYGLASS_LEFT_AS_RIGHT.offset.z),
  rotation: mirrorHandRotationInPlace(SPYGLASS_LEFT_AS_RIGHT.rotation),
};
/** Leather-grip stations: middle carry and eyepiece end. The objective tube spans 0.225–0.32 m. */
const SPYGLASS_CARRY_STATION = 0.16, SPYGLASS_EYEPIECE_STATION = 0.08, SPYGLASS_OBJECTIVE_STATION = 0.27;

/**
 * Raised, the right hand holds the eyepiece end and the left the objective end.
 * Lowered, the right hand alone carries it at the middle; callers blend the left
 * hand toward its free pose by `1 - raised`. Rotations use the eye-frame hand.
 */
export function spyglassGripToRef(spyglass: TransformNode, side: number, raised: number, contact: Vector3, rotation: Quaternion): void {
  const frame = side < 0 ? SPYGLASS_LEFT : SPYGLASS_RIGHT;
  const station = side < 0 ? SPYGLASS_OBJECTIVE_STATION : SPYGLASS_CARRY_STATION + (SPYGLASS_EYEPIECE_STATION - SPYGLASS_CARRY_STATION) * raised;
  contact.set(frame.offset.x, frame.offset.y, station);
  Vector3.TransformCoordinatesToRef(contact, spyglass.computeWorldMatrix(true), contact);
  spyglass.absoluteRotationQuaternion.multiplyToRef(frame.rotation, rotation);
}

/** Actual lower side-ring handles; all chest tiers share these authored points. */
export const CHEST_CONTACTS = [new Vector3(-0.47, 0.216, 0), new Vector3(0.47, 0.216, 0)] as const;
export const REMOTE_CHEST_CONTACTS = [new Vector3(-0.47, -0.034, 0), new Vector3(0.47, -0.034, 0)] as const;

export function chestGripToRef(root: TransformNode, side: number, out: Vector3, remote = false): void {
  Vector3.TransformCoordinatesToRef((remote ? REMOTE_CHEST_CONTACTS : CHEST_CONTACTS)[side < 0 ? 0 : 1], root.computeWorldMatrix(true), out);
}

/** Pure allocation-free IK. pole is a world-space point, not a direction. */
export function solveTwoBoneToRef(shoulder: Vector3, wrist: Vector3, pole: Vector3, upperLength: number, lowerLength: number, elbow: Vector3, resolvedWrist: Vector3): void {
  let dx = wrist.x - shoulder.x, dy = wrist.y - shoulder.y, dz = wrist.z - shoulder.z;
  const requested = Math.hypot(dx, dy, dz);
  if (requested > 1e-9) { dx /= requested; dy /= requested; dz /= requested; }
  else { dx = 0; dy = 0; dz = 1; }
  const distance = Math.max(Math.abs(upperLength - lowerLength) + 0.001, Math.min(upperLength + lowerLength - 0.001, requested));
  let px = pole.x - shoulder.x, py = pole.y - shoulder.y, pz = pole.z - shoulder.z;
  const projection = px * dx + py * dy + pz * dz;
  px -= dx * projection; py -= dy * projection; pz -= dz * projection;
  let poleLength = Math.hypot(px, py, pz);
  if (poleLength < 1e-5) {
    // Downward pole unless the target is vertical, then deterministic outward.
    px = Math.abs(dy) < 0.9 ? 0 : 1;
    py = Math.abs(dy) < 0.9 ? -1 : 0;
    pz = 0;
    const along = px * dx + py * dy;
    px -= dx * along; py -= dy * along; pz -= dz * along;
    poleLength = Math.hypot(px, py, pz);
  }
  const along = (upperLength * upperLength - lowerLength * lowerLength + distance * distance) / (2 * distance);
  const bend = Math.sqrt(Math.max(0, upperLength * upperLength - along * along)) / poleLength;
  elbow.set(shoulder.x + dx * along + px * bend, shoulder.y + dy * along + py * bend, shoulder.z + dz * along + pz * bend);
  resolvedWrist.set(shoulder.x + dx * distance, shoulder.y + dy * distance, shoulder.z + dz * distance);
}

/** Shared joint placement; each segment is a sibling so no scale/stretch is needed. */
class TwoBoneRig {
  readonly shoulder = new Vector3();
  readonly elbow = new Vector3();
  readonly wrist = new Vector3();
  contactError = 0;
  protected readonly parent: TransformNode;
  protected readonly inverse = new Matrix();
  protected readonly rotationMatrix = new Matrix();
  protected readonly inverseRotation = Quaternion.Identity();
  protected readonly local = new Vector3();
  protected readonly direction = new Vector3();
  protected readonly axis: Vector3;

  constructor(readonly upper: TransformNode, readonly lower: TransformNode, readonly upperLength: number, readonly lowerLength: number) {
    this.parent = upper.parent as TransformNode;
    this.axis = lower.position.clone().normalize();
    lower.parent = this.parent;
    upper.rotationQuaternion = Quaternion.Identity();
    lower.rotationQuaternion = Quaternion.Identity();
    upper.scaling.setAll(1);
    lower.scaling.setAll(1);
  }

  protected applyJoints(shoulder: Vector3, wrist: Vector3, pole: Vector3, compensation: number): void {
    this.shoulder.copyFrom(shoulder);
    wrist.subtractToRef(shoulder, this.direction);
    const distance = this.direction.length();
    const extra = Math.min(compensation, Math.max(0, distance - this.upperLength - this.lowerLength + 0.001));
    if (extra > 0) {
      this.direction.scaleToRef(extra / distance, this.local);
      this.shoulder.addInPlace(this.local);
    }
    solveTwoBoneToRef(this.shoulder, wrist, pole, this.upperLength, this.lowerLength, this.elbow, this.wrist);
    this.contactError = Vector3.Distance(wrist, this.wrist);
    const world = this.parent.computeWorldMatrix(true);
    world.invertToRef(this.inverse);
    Quaternion.FromRotationMatrixToRef(world, this.inverseRotation);
    this.inverseRotation.conjugateInPlace();
    this.place(this.upper, this.shoulder, this.elbow);
    this.place(this.lower, this.elbow, this.wrist);
  }

  private place(node: TransformNode, start: Vector3, end: Vector3): void {
    Vector3.TransformCoordinatesToRef(start, this.inverse, node.position);
    end.subtractToRef(start, this.direction);
    Vector3.TransformNormalToRef(this.direction, this.inverse, this.direction);
    this.direction.normalize();
    Quaternion.FromUnitVectorsToRef(this.axis, this.direction, node.rotationQuaternion!);
    node.scaling.setAll(1);
  }
}

export type HandPose = "open" | "relaxed" | "tool" | "rim" | "support" | "barrel" | "swim" | "pistol";
export const HAND_POSES = {
  open: { proximal: 0, distal: 0, thumb: 0.15 },
  relaxed: { proximal: 0.25, distal: 0.20, thumb: 0.30 },
  tool: { proximal: 0.95, distal: 1.25, thumb: 0.75 },
  rim: { proximal: 1.05, distal: 1.25, thumb: 0.85 },
  support: { proximal: 0.55, distal: 0.65, thumb: 0.50 },
  // Wide barrel: shallow knuckles and curled tips, with the thumb outside the brass.
  barrel: { proximal: 0.50, distal: 1.00, thumb: 0.15 },
  swim: { proximal: 0.10, distal: 0.10, thumb: 0.15 },
  pistol: { proximal: 0.35, distal: 1.10, thumb: 0.70 },
} as const;
const DIGITS = ["thumb", "index", "middle", "ring", "little"] as const;
const FINGER_MULTIPLIERS = [1, 0.95, 1, 1.03, 1.08] as const;
// Shorter lower digits meet the swept heel later, rather than curling into wood.
const PISTOL_PROXIMAL = [0, 0, 0.35, 0.30, 0.20] as const;
const PISTOL_DISTAL = [0, 0, 1.10, 1.15, 1.00] as const;
const THUMB_EYE_AXIS = new Vector3(0, 1, 0);
const THUMB_HANGING_AXIS = new Vector3(0, 0, 1);
const CURL_AXIS = new Vector3(1, 0, 0);
const smooth = (t: number): number => { const p = Math.max(0, Math.min(1, t)); return p * p * (3 - 2 * p); };
const ARM_FRAME_TRANSITION = 0.18;

/** Incremental smoothstep reaches the exact target and can restart from the rendered frame. */
function armFrameBlend(before: number, after: number): number {
  if (after >= ARM_FRAME_TRANSITION) return 1;
  const weight = smooth(before / ARM_FRAME_TRANSITION);
  return (smooth(after / ARM_FRAME_TRANSITION) - weight) / (1 - weight);
}

/** The same angular rate bound covers axial roll and elbow-plane turns, even at a moving target. */
function armFrameStep(blend: number, angle: number, dt: number): number {
  const maxStep = Math.PI * 8 * dt;
  return angle > maxStep ? Math.min(blend, maxStep / angle) : blend;
}

/** Reusable for near hands and remote arms. World hand rotations are explicit. */
export class ArticulatedArm extends TwoBoneRig {
  readonly hand: TransformNode;
  readonly contact = new Vector3();
  readonly gripLocal: Vector3;
  readonly digits: readonly { proximal: TransformNode; distal: TransformNode }[];
  /** Opt-in wrist transport; source changes ease without moving the wrist, contact or tool. */
  forearmPronation = false;
  private readonly palm: TransformNode;
  private readonly open: TransformNode;
  private readonly grip: TransformNode;
  private readonly targetWrist = new Vector3();
  private readonly forearmSwing = Quaternion.Identity();
  private readonly forearmRotation = Quaternion.Identity();
  private readonly previousForearm = Quaternion.Identity();
  private readonly parentTransport = new Matrix();
  private readonly previousDirection = new Vector3();
  private readonly poleForward = new Vector3();
  private readonly polePreviousForward = new Vector3();
  private readonly polePrevious = new Vector3();
  private readonly poleDesired = new Vector3();
  private readonly poleCross = new Vector3();
  private readonly blendedPole = new Vector3();
  private placed = false;
  private poleReleasing = false;
  private framePending = false;
  private previousGripPole = false;
  private previousPronation = false;
  private frameTransition = ARM_FRAME_TRANSITION;
  private readonly thumbYaw = Quaternion.Identity();
  private readonly thumbCurl = Quaternion.Identity();
  private readonly side: number;
  private pose: HandPose = "open";
  private poseTime = 0;
  private proximal = 0;
  private distal = 0;
  private thumb = 0.15;
  private startProximal = 0;
  private startDistal = 0;
  private startThumb = 0.15;
  private detail = true;
  private readonly thumbAxis: Vector3;
  private readonly indexTarget = new Vector3();
  private readonly indexElbow = new Vector3();
  private readonly indexTip = new Vector3();
  private readonly indexPole = new Vector3();
  private readonly indexDirection = new Vector3();
  private readonly indexAxis: Vector3;
  private readonly indexInverse = new Matrix();

  constructor(model: ModelInstance, suffix: "L" | "R", frame: "eye" | "hanging") {
    super(model.part(`upperArm${suffix}`), model.part(`forearm${suffix}`), ARM_UPPER_LENGTH, ARM_LOWER_LENGTH);
    this.side = suffix === "L" ? -1 : 1;
    this.hand = model.part(`hand${suffix}`);
    this.hand.parent = this.parent;
    this.hand.rotationQuaternion = Quaternion.Identity();
    this.hand.scaling.setAll(1);
    this.gripLocal = (frame === "eye" ? HAND_GRIP : HANGING_HAND_GRIP).clone();
    this.thumbAxis = frame === "eye" ? THUMB_EYE_AXIS : THUMB_HANGING_AXIS;
    this.indexAxis = (frame === "eye" ? new Vector3(0, 0, 1) : new Vector3(0, -1, 0));
    this.palm = model.part(`handPalm${suffix}`);
    this.open = model.part(`handOpen${suffix}`);
    this.grip = model.part(`handGrip${suffix}`);
    this.digits = DIGITS.map((name) => {
      const proximal = model.part(`${name}Proximal${suffix}`);
      const distal = model.part(`${name}Distal${suffix}`);
      proximal.rotationQuaternion = Quaternion.Identity();
      distal.rotationQuaternion = Quaternion.Identity();
      return { proximal, distal };
    });
    this.setHandPose("open", 0.08);
    this.setDetail(true);
  }

  /** Prefer an elbow behind the grip along the fingers, so the wrist need not fold back. */
  gripPoleToRef(contact: Vector3, handRotation: Quaternion, out: Vector3): void {
    Matrix.FromQuaternionToRef(handRotation, this.rotationMatrix);
    this.axis.scaleToRef(this.lowerLength, this.local);
    this.local.addInPlace(this.gripLocal);
    Vector3.TransformNormalToRef(this.local, this.rotationMatrix, this.local);
    contact.subtractToRef(this.local, out);
  }

  /** Desired contact is the palm's transverse grip anchor, not the wrist. dt advances frame transitions only. */
  applyWorld(shoulder: Vector3, contact: Vector3, pole: Vector3, handRotation: Quaternion, dt: number, gripPole: boolean): void {
    const step = Math.max(0, dt);
    const poleChanged = gripPole !== this.previousGripPole;
    if (this.placed && (poleChanged || this.forearmPronation !== this.previousPronation)) {
      this.frameTransition = 0;
      this.framePending = true;
    }
    if (poleChanged) this.poleReleasing = !gripPole;
    const before = this.frameTransition;
    this.frameTransition = Math.min(ARM_FRAME_TRANSITION, this.frameTransition + step);
    const transitioning = this.placed && (before < ARM_FRAME_TRANSITION || this.framePending);
    const blend = transitioning ? armFrameBlend(before, this.frameTransition) : 1;
    if (this.placed && (transitioning || gripPole || this.poleReleasing)) {
      // Carry the rendered frame with its parent before easing relative joint changes.
      // World-space history otherwise leaves an elbow/roll behind when a deck or body turns.
      this.inverse.multiplyToRef(this.parent.computeWorldMatrix(true), this.parentTransport);
      Vector3.TransformCoordinatesToRef(this.shoulder, this.parentTransport, this.shoulder);
      Vector3.TransformCoordinatesToRef(this.elbow, this.parentTransport, this.elbow);
      Vector3.TransformCoordinatesToRef(this.wrist, this.parentTransport, this.wrist);
    }
    if (transitioning) {
      // One frame transition for roll and elbow-side requests; neither changes hand/tool transforms.
      Quaternion.FromRotationMatrixToRef(this.parent.getWorldMatrix(), this.previousForearm);
      this.previousForearm.multiplyToRef(this.lower.rotationQuaternion!, this.previousForearm);
      this.wrist.subtractToRef(this.elbow, this.previousDirection);
      this.previousDirection.normalize();
    }
    Matrix.FromQuaternionToRef(handRotation, this.rotationMatrix);
    Vector3.TransformNormalToRef(this.gripLocal, this.rotationMatrix, this.local);
    contact.subtractToRef(this.local, this.targetWrist);
    const resolvedPole = this.placed && (gripPole || this.poleReleasing)
      ? this.transitionPole(shoulder, pole, transitioning ? blend : 1 - Math.exp(-step / 0.08), step)
      : pole;
    this.applyJoints(shoulder, this.targetWrist, resolvedPole, MAX_SHOULDER_REACH);
    if (this.forearmPronation || transitioning) {
      this.wrist.subtractToRef(this.elbow, this.direction);
      this.direction.normalize();
      if (this.forearmPronation) {
        Vector3.TransformNormalToRef(this.axis, this.rotationMatrix, this.local);
        Quaternion.FromUnitVectorsToRef(this.local, this.direction, this.forearmSwing);
        this.forearmSwing.multiplyToRef(handRotation, this.forearmRotation);
      } else {
        this.inverseRotation.conjugateToRef(this.forearmRotation);
        this.forearmRotation.multiplyToRef(this.lower.rotationQuaternion!, this.forearmRotation);
      }
      if (transitioning) {
        Quaternion.FromUnitVectorsToRef(this.previousDirection, this.direction, this.forearmSwing);
        this.forearmSwing.multiplyToRef(this.previousForearm, this.previousForearm);
        const angle = 2 * Math.acos(Math.min(1, Math.abs(Quaternion.Dot(this.previousForearm, this.forearmRotation))
          / Math.sqrt(this.previousForearm.lengthSquared() * this.forearmRotation.lengthSquared())));
        const frameStep = armFrameStep(blend, angle, step);
        Quaternion.SlerpToRef(this.previousForearm, this.forearmRotation, frameStep, this.forearmRotation);
        this.framePending = frameStep < 1 && angle > 1e-7;
      }
      this.inverseRotation.multiplyToRef(this.forearmRotation, this.lower.rotationQuaternion!);
    }
    this.previousPronation = this.forearmPronation;
    this.previousGripPole = gripPole;
    this.placed = true;
    Vector3.TransformCoordinatesToRef(this.wrist, this.inverse, this.hand.position);
    this.inverseRotation.multiplyToRef(handRotation, this.hand.rotationQuaternion!);
    Vector3.TransformNormalToRef(this.gripLocal, this.rotationMatrix, this.local);
    this.wrist.addToRef(this.local, this.contact);
    this.hand.computeWorldMatrix(true);
  }

  private transitionPole(shoulder: Vector3, pole: Vector3, blend: number, dt: number): Vector3 {
    this.targetWrist.subtractToRef(shoulder, this.poleForward);
    if (this.poleForward.lengthSquared() > 1e-18) this.poleForward.normalize();
    else this.poleForward.set(0, 0, 1);
    pole.subtractToRef(shoulder, this.poleDesired);
    this.poleForward.scaleToRef(Vector3.Dot(this.poleDesired, this.poleForward), this.poleCross);
    this.poleDesired.subtractInPlace(this.poleCross);
    if (this.poleDesired.lengthSquared() < 1e-10) {
      // Match solveTwoBoneToRef's deterministic fallback at an axial pole.
      this.poleDesired.set(Math.abs(this.poleForward.y) < 0.9 ? 0 : 1, Math.abs(this.poleForward.y) < 0.9 ? -1 : 0, 0);
      this.poleForward.scaleToRef(Vector3.Dot(this.poleDesired, this.poleForward), this.poleCross);
      this.poleDesired.subtractInPlace(this.poleCross);
    }
    this.poleDesired.normalize();
    this.wrist.subtractToRef(this.shoulder, this.polePreviousForward);
    this.polePreviousForward.normalize();
    this.elbow.subtractToRef(this.shoulder, this.polePrevious);
    this.polePreviousForward.scaleToRef(Vector3.Dot(this.polePrevious, this.polePreviousForward), this.poleCross);
    this.polePrevious.subtractInPlace(this.poleCross).normalize();
    Quaternion.FromUnitVectorsToRef(this.polePreviousForward, this.poleForward, this.forearmSwing);
    this.polePrevious.applyRotationQuaternionToRef(this.forearmSwing, this.polePrevious);
    Vector3.CrossToRef(this.polePrevious, this.poleDesired, this.poleCross);
    const remainingAngle = Math.atan2(Vector3.Dot(this.poleCross, this.poleForward), Vector3.Dot(this.polePrevious, this.poleDesired));
    if (Math.abs(remainingAngle) < 1e-7) {
      this.poleReleasing = false;
      return pole;
    }
    const frameStep = armFrameStep(blend, Math.abs(remainingAngle), dt);
    if (frameStep === 1) {
      this.poleReleasing = false;
      return pole;
    }
    const angle = remainingAngle * frameStep;
    Vector3.CrossToRef(this.poleForward, this.polePrevious, this.poleCross);
    this.polePrevious.scaleToRef(Math.cos(angle), this.blendedPole);
    this.poleCross.scaleInPlace(Math.sin(angle));
    this.blendedPole.addInPlace(this.poleCross).addInPlace(shoulder);
    return this.blendedPole;
  }

  /** Blend actual joints over .08s, starting from the currently rendered pose. */
  setHandPose(pose: HandPose, dt: number): void {
    if (pose === this.pose && this.poseTime >= 0.08) return;
    if (pose !== this.pose) {
      this.pose = pose;
      this.poseTime = 0;
      this.startProximal = this.proximal; this.startDistal = this.distal; this.startThumb = this.thumb;
    }
    this.poseTime = Math.min(0.08, this.poseTime + Math.max(0, dt));
    const blend = smooth(this.poseTime / 0.08), target = HAND_POSES[pose];
    this.proximal = this.startProximal + (target.proximal - this.startProximal) * blend;
    this.distal = this.startDistal + (target.distal - this.startDistal) * blend;
    this.thumb = this.startThumb + (target.thumb - this.startThumb) * blend;
    this.renderHandPose();
  }

  /** Lower digits wrap wood; the index alone follows the moving trigger. */
  setPistolGrip(triggerContact: Vector3, dt: number): void {
    this.setHandPose("pistol", dt);
    this.hand.computeWorldMatrix(true).invertToRef(this.indexInverse);
    Vector3.TransformCoordinatesToRef(triggerContact, this.indexInverse, this.indexTarget);
    const digit = this.digits[1]!, root = digit.proximal.position;
    this.indexPole.copyFrom(root);
    if (this.indexAxis.z === 1) { this.indexPole.y -= 0.05; this.indexPole.z += 0.025; }
    else { this.indexPole.z -= 0.05; this.indexPole.y -= 0.025; }
    solveTwoBoneToRef(root, this.indexTarget, this.indexPole, 0.033, 0.026, this.indexElbow, this.indexTip);
    this.indexElbow.subtractToRef(root, this.indexDirection);
    this.indexDirection.normalize();
    Quaternion.FromUnitVectorsToRef(this.indexAxis, this.indexDirection, digit.proximal.rotationQuaternion!);
    Matrix.FromQuaternionToRef(digit.proximal.rotationQuaternion!, this.indexInverse);
    this.indexInverse.invertToRef(this.indexInverse);
    this.indexTip.subtractToRef(this.indexElbow, this.indexDirection);
    Vector3.TransformNormalToRef(this.indexDirection, this.indexInverse, this.indexDirection);
    this.indexDirection.normalize();
    Quaternion.FromUnitVectorsToRef(this.indexAxis, this.indexDirection, digit.distal.rotationQuaternion!);
  }

  /** Contact choreography already blends continuously; do not add joint lag. */
  setPistolSupport(proximal: number, distal: number, thumb: number): void {
    this.pose = "support"; this.poseTime = 0;
    this.proximal = proximal; this.distal = distal; this.thumb = thumb;
    this.renderHandPose();
  }

  private renderHandPose(): void {
    for (let i = 0; i < this.digits.length; i++) {
      const digit = this.digits[i]!;
      if (i === 0) {
        Quaternion.RotationAxisToRef(this.thumbAxis, this.side * 0.5 * this.closure, this.thumbYaw);
        Quaternion.RotationAxisToRef(CURL_AXIS, this.thumb, this.thumbCurl);
        this.thumbYaw.multiplyToRef(this.thumbCurl, digit.proximal.rotationQuaternion!);
        Quaternion.RotationAxisToRef(CURL_AXIS, this.thumb * 1.15, digit.distal.rotationQuaternion!);
      } else {
        const pistolDigit = this.pose === "pistol" && i >= 2;
        Quaternion.RotationAxisToRef(CURL_AXIS, pistolDigit ? PISTOL_PROXIMAL[i]! : this.proximal * FINGER_MULTIPLIERS[i]!, digit.proximal.rotationQuaternion!);
        Quaternion.RotationAxisToRef(CURL_AXIS, pistolDigit ? PISTOL_DISTAL[i]! : this.distal * FINGER_MULTIPLIERS[i]!, digit.distal.rotationQuaternion!);
      }
    }
    this.updateVisibility();
  }

  get closure(): number { return this.pose === "pistol" ? 1 : Math.max(0, Math.min(1, this.proximal / HAND_POSES.tool.proximal)); }

  /** Local hands always use true; remotes use distance / magnification <= 30. */
  setDetail(detail: boolean): void {
    if (detail === this.detail) return;
    this.detail = detail;
    this.updateVisibility();
  }

  private updateVisibility(): void {
    this.palm.setEnabled(this.detail);
    for (let i = 0; i < this.digits.length; i++) this.digits[i]!.proximal.setEnabled(this.detail);
    this.open.setEnabled(!this.detail && this.closure < 0.5);
    this.grip.setEnabled(!this.detail && this.closure >= 0.5);
  }
}

/** Ankle target is .08m above the authored sole, not the physical feet root. */
export class ArticulatedLeg extends TwoBoneRig {
  readonly ankle: TransformNode;
  constructor(model: ModelInstance, suffix: "L" | "R") {
    super(model.part(`leg${suffix}`), model.part(`knee${suffix}`), LEG_UPPER_LENGTH, LEG_LOWER_LENGTH);
    this.ankle = model.part(`ankle${suffix}`);
    this.ankle.parent = this.parent;
    this.ankle.rotationQuaternion = Quaternion.Identity();
    this.ankle.scaling.setAll(1);
  }
  applyWorld(hip: Vector3, ankle: Vector3, pole: Vector3, footRotation?: Quaternion): void {
    this.applyJoints(hip, ankle, pole, 0);
    Vector3.TransformCoordinatesToRef(this.wrist, this.inverse, this.ankle.position);
    // Keep the sole level in the body's yaw frame instead of tilting with the shin.
    if (footRotation) this.inverseRotation.multiplyToRef(footRotation, this.ankle.rotationQuaternion!);
    else this.ankle.rotationQuaternion!.set(0, 0, 0, 1);
  }
}

export interface SwordPose { contact: Vector3; yaw: number; pitch: number; roll: number }
const SWORD_IDLE = [0.30, -0.25, 0.62, -0.2, 0.45, Math.PI / 2] as const;
/** Relaxed cutlass offhand, authored for the right hand: callers place it at `side·x` and mirror the rotation. Guard overrides it. */
export const SWORD_OFFHAND = { x: 0.25, y: -0.24, z: 0.58, yaw: -0.2, pitch: 0.15, roll: -0.65 } as const;

/** Hand pose in a rig's action frame: grip-anchor contact plus Yaw/Pitch/Roll. */
export interface SwordGuardHand { x: number; y: number; z: number; yaw: number; pitch: number; roll: number }

/**
 * Defensive guard solved from the blade, not tuned Euler angles: the hilt sits in
 * front of the sword shoulder and the blade rises across the front of the chest
 * toward the off side, edge facing the attacker. The off hand braces its open
 * palm against the spine. Every rig composes the sword hand as
 * YPR · Rz(π) and mounts the blade (+y, edge −x) with SWORD_GRIP_ROTATION.
 */
function solveSwordGuard(hilt: Vector3, blade: Vector3, brace: number): { sword: SwordGuardHand; offhand: SwordGuardHand } {
  const along = blade.normalizeToNew();
  const edge = new Vector3(0, 0, 1).subtractInPlace(along.scale(along.z)).normalize();
  const spine = edge.negate();
  const toHand = (x: Vector3, y: Vector3, z: Vector3, after: Quaternion, at: Vector3): SwordGuardHand => {
    const rotation = Quaternion.FromRotationMatrix(Matrix.FromXYZAxesToRef(x, y, z, new Matrix())).multiply(after);
    const angles = rotation.toEulerAngles();
    return { x: at.x, y: at.y, z: at.z, yaw: angles.y, pitch: angles.x, roll: angles.z };
  };
  const swordToHand = Quaternion.Inverse(SWORD_GRIP_ROTATION).multiply(Quaternion.Inverse(Quaternion.RotationYawPitchRoll(0, 0, Math.PI)));
  const sword = toHand(spine, along, Vector3.Cross(spine, along), swordToHand, hilt);
  // Eye-frame hand axes: palm faces −y and the fingers run along +z; here they follow the blade.
  const palm = hilt.add(along.scale(brace)).addInPlace(spine.scale(0.045));
  const offhand = toHand(Vector3.Cross(spine, along), spine, along, Quaternion.Identity(), palm);
  return { sword, offhand };
}

export const SWORD_GUARD = solveSwordGuard(new Vector3(0.16, -0.20, 0.42), new Vector3(-0.78, 0.45, 0.30), 0.34);
// Cross-body grips keep the fingers aimed along the forearm, not back toward the elbow.
const SWORD_POSES = [
  { ready: [0.42, -0.03, 0.43, -0.8, 0.4, 0.8], impact: [-0.12, -0.205, 0.52, -0.5, 0.1, -0.7], follow: [-0.075, -0.28, 0.515, -0.55, 0.2, -0.9] },
  { ready: [-0.10, -0.06, 0.45, -0.65, 0.3, -0.7], impact: [0.40, -0.19, 0.64, -0.9, 0.1, 0.8], follow: [0.34, -0.28, 0.52, -1.05, 0.2, 0.95] },
  { ready: [0.20, 0.12, 0.40, 0, -0.8, 0.1], impact: [0.12, -0.38, 0.68, 0, 0.9, 0.1], follow: [0.12, -0.40, 0.53, 0, 1.05, 0.1] },
] as const;

/** Eye-frame grip-origin anchor. The active midpoint remains synchronized with authority. */
export function sampleSwordPoseToRef(step: number, elapsed: number, out: SwordPose): void {
  const index = step === 1 || step === 2 ? step : 0;
  const timing = MELEE.combo[index], pose = SWORD_POSES[index];
  let from: readonly number[] = SWORD_IDLE, to: readonly number[] = SWORD_IDLE, blend = 0;
  if (elapsed >= 0 && elapsed < timing.windup) {
    to = pose.ready; blend = smooth(elapsed / timing.windup);
  } else if (elapsed >= timing.windup && elapsed < timing.windup + timing.active / 2) {
    from = pose.ready; to = pose.impact; blend = smooth((elapsed - timing.windup) / (timing.active / 2));
  } else if (elapsed >= timing.windup + timing.active / 2 && elapsed < timing.windup + timing.active) {
    from = pose.impact; to = pose.follow; blend = smooth((elapsed - timing.windup - timing.active / 2) / (timing.active / 2));
  } else if (elapsed >= timing.windup + timing.active) {
    from = pose.follow; blend = smooth((elapsed - timing.windup - timing.active) / timing.recovery);
  }
  out.contact.set(from[0]! + (to[0]! - from[0]!) * blend, from[1]! + (to[1]! - from[1]!) * blend, from[2]! + (to[2]! - from[2]!) * blend);
  out.yaw = from[3]! + (to[3]! - from[3]!) * blend;
  out.pitch = from[4]! + (to[4]! - from[4]!) * blend;
  out.roll = from[5]! + (to[5]! - from[5]!) * blend;
}

export interface PistolPose {
  contact: Vector3;
  supportFrom: number;
  supportTo: number;
  supportBlend: number;
  supportProximal: number;
  supportDistal: number;
  supportThumb: number;
  yaw: number;
  pitch: number;
  roll: number;
  hammer: number;
  trigger: number;
  cylinder: number;
  indexOffTrigger: number;
  swing: number;
  ejector: number;
  ejection: number;
  loader: number;
  loaderCarry: number;
  loaderTwist: number;
  reloadRounds: number;
  roundsSeated: boolean;
  loaderVisible: boolean;
  latch: number;
  rounds: number;
  casesVisible: boolean;
}

export function createPistolPose(): PistolPose {
  return { contact: new Vector3(), supportFrom: 0, supportTo: 0, supportBlend: 0, supportProximal: 0.45, supportDistal: 0.8, supportThumb: 0.55, yaw: 0, pitch: 0, roll: 0, hammer: 0, trigger: 0, indexOffTrigger: 0, cylinder: 0, swing: 0, ejector: 0, ejection: 0, loader: 0, loaderCarry: 0, loaderTwist: 0, reloadRounds: 0, roundsSeated: false, loaderVisible: false, latch: 0, rounds: 6, casesVisible: true };
}

function pistolSupportStage(progress: number, marker: string, yaw: number, pitch: number, roll: number, proximal: number, distal: number, thumb: number, fingertip = false) {
  const p = proximal * FINGER_MULTIPLIERS[1], d = distal * FINGER_MULTIPLIERS[1];
  return {
    progress, marker, rotation: Quaternion.RotationYawPitchRoll(yaw, pitch, roll), proximal, distal, thumb,
    offset: fingertip ? new Vector3(0.030, 0.009 - 0.033 * Math.sin(p) - 0.026 * Math.sin(p + d) - HAND_GRIP.y, 0.068 + 0.033 * Math.cos(p) + 0.026 * Math.cos(p + d) - HAND_GRIP.z) : Vector3.Zero(),
  };
}

// Marker-relative contact and palm orientation, not copied weapon coordinates.
// Repeated contacts stay attached to their moving mechanical part while working.
const PISTOL_RELOAD_CONTACTS = [
  pistolSupportStage(0, "supportGrip", 0, 0, Math.PI / 2, 0.45, 0.80, 0.55),
  pistolSupportStage(0.10, "latchContact", -0.25, 0, Math.PI / 2, 0.18, 0.35, 0.25, true),
  pistolSupportStage(0.16, "latchContact", -0.25, 0, Math.PI / 2, 0.18, 0.35, 0.25, true),
  pistolSupportStage(0.20, "cylinderContact", 0, 0, Math.PI / 2, 0.55, 0.80, 0.55),
  pistolSupportStage(0.30, "cylinderContact", 0, 0, Math.PI / 2, 0.55, 0.80, 0.55),
  pistolSupportStage(0.36, "ejectorContact", Math.PI, 0, -Math.PI / 2, 0.25, 0.55, 0.30, true),
  pistolSupportStage(0.46, "ejectorContact", Math.PI, 0, -Math.PI / 2, 0.25, 0.55, 0.30, true),
  pistolSupportStage(0.53, "loaderContact", -Math.PI / 2, 0, 0, 0.65, 1.35, 0.75),
  pistolSupportStage(0.81, "loaderContact", -Math.PI / 2, 0, 0, 0.65, 1.35, 0.75),
  pistolSupportStage(0.86, "cylinderContact", 0, 0, Math.PI / 2, 0.55, 0.80, 0.55),
  pistolSupportStage(0.94, "cylinderContact", 0, 0, Math.PI / 2, 0.55, 0.80, 0.55),
  pistolSupportStage(1, "supportGrip", 0, 0, Math.PI / 2, 0.45, 0.80, 0.55),
] as const;

/** Shared allocation-free revolver choreography; recoil falls from one to zero. */
export function samplePistolPoseToRef(rounds: number, reserve: number, aimBlend: number, reloadProgress: number, recoil: number, out: PistolPose, aimGripHeight = -0.170): void {
  const aim = smooth(aimBlend), shot = 1 - Math.max(0, Math.min(1, recoil));
  // A short rise, rearward impulse, then a longer damped return. Both endpoints
  // are the ready pose, including the first frame of a confirmed shot.
  const kick = recoil <= 0 ? 0 : shot < 0.09 ? smooth(shot / 0.09) : shot < 0.26 ? 1 - 0.28 * smooth((shot - 0.09) / 0.17) : 0.72 * (1 - smooth((shot - 0.26) / 0.74));
  const pull = recoil <= 0 ? 0 : smooth(shot / 0.055) * (1 - smooth((shot - 0.22) / 0.50));
  out.rounds = Math.max(0, Math.min(6, Math.floor(rounds)));
  out.contact.set(0.22 * (1 - aim), -0.20 + (aimGripHeight + 0.20) * aim + 0.017 * kick, 0.48 - 0.05 * aim - 0.072 * kick);
  out.yaw = -0.018 * kick; out.pitch = -0.24 * kick; out.roll = 0.035 * kick;
  out.supportFrom = 0; out.supportTo = 0; out.supportBlend = 0;
  out.supportProximal = 0.45; out.supportDistal = 0.80; out.supportThumb = 0.55;
  out.hammer = recoil <= 0 ? 0 : smooth(shot / 0.04) * (1 - smooth((shot - 0.045) / 0.055));
  out.trigger = pull;
  out.indexOffTrigger = 0;
  const index = 6 - out.rounds;
  out.cylinder = (index - (recoil > 0 && index > 0 ? 1 - smooth(shot / 0.12) : 0)) * Math.PI / 3;
  out.swing = 0; out.ejector = 0; out.ejection = 0; out.loader = 0; out.loaderCarry = 0; out.loaderTwist = 0;
  out.reloadRounds = Math.min(6 - out.rounds, Math.max(0, Math.floor(reserve)));
  out.roundsSeated = false; out.loaderVisible = false; out.latch = 0; out.casesVisible = true;
  if (reloadProgress < 0) return;
  const p = Math.max(0, Math.min(1, reloadProgress));
  const present = smooth(p / 0.10) * (1 - smooth((p - 0.94) / 0.06));
  out.contact.x += (0.10 - out.contact.x) * present;
  out.contact.y += (-0.18 - out.contact.y) * present;
  out.contact.z += (0.32 - out.contact.z) * present;
  out.yaw = -0.55 * present; out.pitch = -0.32 * present; out.roll = 0.28 * present;
  out.hammer = 0; out.trigger = 0; out.indexOffTrigger = present;
  // A mechanism moves only after its working hand has made contact.
  out.swing = smooth((p - 0.20) / 0.10) * (1 - smooth((p - 0.86) / 0.08));
  out.latch = smooth((p - 0.10) / 0.04) * (1 - smooth((p - 0.30) / 0.03));
  out.ejector = smooth((p - 0.36) / 0.05) * (1 - smooth((p - 0.41) / 0.05));
  out.ejection = smooth((p - 0.39) / 0.13);
  out.casesVisible = p < 0.52;
  // Align empty chambers before the loader arrives; firing indexing is unchanged.
  out.cylinder = index * Math.PI / 3 * (1 - smooth((p - 0.50) / 0.11));
  // Once the crane is closed, return onto the next live chamber even with scarce reserve.
  out.cylinder += (6 - out.rounds - out.reloadRounds) * Math.PI / 3 * smooth((p - 0.94) / 0.06);
  out.loaderCarry = smooth((p - 0.53) / 0.08) * (1 - smooth((p - 0.74) / 0.07));
  out.loader = smooth((p - 0.61) / 0.06) * (1 - smooth((p - 0.70) / 0.04));
  out.loaderTwist = smooth((p - 0.67) / 0.03);
  out.roundsSeated = p >= 0.70;
  // Pickup and stow are below the frame; the hand stays attached while visible.
  out.loaderVisible = p >= 0.53 && p < 0.81;
  for (let i = 1; i < PISTOL_RELOAD_CONTACTS.length; i++) {
    const to = PISTOL_RELOAD_CONTACTS[i]!, from = PISTOL_RELOAD_CONTACTS[i - 1]!;
    if (p > to.progress) continue;
    const blend = smooth((p - from.progress) / (to.progress - from.progress));
    out.supportFrom = i - 1; out.supportTo = i; out.supportBlend = blend;
    out.supportProximal = from.proximal + (to.proximal - from.proximal) * blend;
    out.supportDistal = from.distal + (to.distal - from.distal) * blend;
    out.supportThumb = from.thumb + (to.thumb - from.thumb) * blend;
    break;
  }
}

/** One mechanism implementation for near and remote instances. */
export class PistolMechanism {
  private readonly hammer: TransformNode;
  private readonly trigger: TransformNode;
  private readonly cylinder: TransformNode;
  private readonly swing: TransformNode;
  private readonly ejector: TransformNode;
  private readonly loader: TransformNode;
  private readonly loaderRelease: TransformNode;
  private readonly loaderStow: TransformNode;
  private readonly loaderRounds: readonly TransformNode[];
  private readonly latch: TransformNode;
  private readonly cartridges: readonly TransformNode[];
  private readonly cases: readonly TransformNode[];
  private readonly caseOrigins: readonly Vector3[];
  readonly rightGrip: Vector3;
  readonly aimGripHeight: number;
  private readonly root: TransformNode;
  private readonly muzzle: TransformNode;
  private readonly triggerContact: TransformNode;
  private readonly indexRest: TransformNode;
  private readonly supportMarkers: readonly TransformNode[];
  private readonly bindEjector: Vector3;
  private readonly bindLatch: Vector3;
  private readonly bindLoader: Vector3;
  private readonly supportA = new Vector3();
  private readonly supportB = new Vector3();
  private readonly supportOffset = new Vector3();
  private readonly supportMatrix = new Matrix();
  private readonly supportRotationA = Quaternion.Identity();
  private readonly supportRotationB = Quaternion.Identity();
  private readonly loaderPickup = new Vector3();
  private readonly loaderInverse = new Matrix();
  private cylinderAngle = 0;
  private initialized = false;

  constructor(model: ModelInstance) {
    this.hammer = model.part("hammer"); this.trigger = model.part("trigger");
    this.cylinder = model.part("cylinder"); this.swing = model.part("cylinderSwing");
    this.ejector = model.part("ejector"); this.loader = model.part("speedloader");
    this.loaderRelease = model.part("loaderRelease"); this.loaderStow = model.part("loaderStow");
    this.loaderRounds = Array.from({ length: 6 }, (_, i) => model.part(`loaderRound${i}`));
    this.latch = model.part("latch");
    this.cartridges = Array.from({ length: 6 }, (_, i) => model.part(`cartridge${i}`));
    this.cases = Array.from({ length: 6 }, (_, i) => model.part(`spentCase${i}`));
    this.caseOrigins = this.cases.map((node) => node.position.clone());
    this.root = model.root;
    this.rightGrip = model.part("rightGrip").position.clone();
    this.aimGripHeight = this.rightGrip.y - model.part("sightRear").position.y;
    this.muzzle = model.part("muzzle"); this.triggerContact = model.part("triggerContact");
    this.indexRest = model.part("indexRest");
    this.supportMarkers = PISTOL_RELOAD_CONTACTS.map((stage) => model.part(stage.marker));
    this.bindEjector = this.ejector.position.clone();
    this.bindLatch = this.latch.position.clone();
    this.bindLoader = this.loader.position.clone();
  }

  apply(pose: PistolPose, dt: number): void {
    this.hammer.rotation.x = -0.65 * pose.hammer;
    this.trigger.rotation.x = 0.34 * pose.trigger;
    // Round patches and shot events may arrive on separate frames. Unwrap and
    // ease the target rather than teleporting a chamber when the patch arrives.
    let delta = pose.cylinder - this.cylinderAngle;
    delta -= Math.round(delta / (Math.PI * 2)) * Math.PI * 2;
    this.cylinderAngle += delta * (this.initialized ? 1 - Math.exp(-Math.max(0, dt) / 0.025) : 1);
    this.initialized = true;
    this.cylinder.rotation.z = this.cylinderAngle;
    this.swing.rotation.z = 1.25 * pose.swing;
    this.ejector.position.z = this.bindEjector.z - 0.060 * pose.ejector;
    this.latch.position.z = this.bindLatch.z + 0.008 * pose.latch;
    this.loader.setEnabled(pose.loaderVisible);
    if (pose.swing > 0) {
      this.swing.computeWorldMatrix(true).invertToRef(this.loaderInverse);
      Vector3.TransformCoordinatesToRef(this.loaderStow.position, this.root.computeWorldMatrix(true), this.loaderPickup);
      Vector3.TransformCoordinatesToRef(this.loaderPickup, this.loaderInverse, this.loaderPickup);
      Vector3.LerpToRef(this.loaderPickup, this.bindLoader, pose.loaderCarry, this.loader.position);
      this.loader.position.z += 0.110 * pose.loader;
      this.loaderRelease.rotation.z = 0.45 * pose.loaderTwist;
    }
    for (let i = 0; i < 6; i++) {
      // Visual transfer precedes the authoritative commit; cancellation restores
      // the replicated rounds without consuming or inventing reserve ammunition.
      // Positive indexing brings chambers 0,5,4,3,2,1 past the barrel in that order.
      const rank = i === 0 ? 6 : i;
      const live = rank <= pose.rounds;
      const replacing = rank > pose.rounds && rank <= pose.rounds + pose.reloadRounds;
      this.cartridges[i]!.setEnabled(live || (replacing && pose.roundsSeated));
      this.loaderRounds[i]!.setEnabled(replacing && !pose.roundsSeated);
      const shell = this.cases[i]!;
      shell.setEnabled(!live && pose.casesVisible);
      shell.position.z = this.caseOrigins[i]!.z - (0.100 + i * 0.007) * pose.ejection;
      const origin = this.caseOrigins[i]!;
      shell.position.x = origin.x + (i - 2.5) * 0.006 * pose.ejection;
      shell.position.y = origin.y - 0.15 * pose.ejection * pose.ejection;
      shell.rotation.x = 0.9 * pose.ejection;
    }
  }

  muzzleToRef(out: Vector3): void { this.muzzle.computeWorldMatrix(true).getTranslationToRef(out); }
  indexToRef(pose: PistolPose, out: Vector3): void {
    this.triggerContact.computeWorldMatrix(true).getTranslationToRef(out);
    this.indexRest.computeWorldMatrix(true).getTranslationToRef(this.supportOffset);
    Vector3.LerpToRef(out, this.supportOffset, pose.indexOffTrigger, out);
  }

  /** Physical fingertip markers are converted to palm anchors before blending. */
  supportToRef(pose: PistolPose, out: Vector3): void {
    this.supportAnchorToRef(pose.supportFrom, this.supportA);
    this.supportAnchorToRef(pose.supportTo, this.supportB);
    Vector3.LerpToRef(this.supportA, this.supportB, pose.supportBlend, out);
  }

  /** Follow the working part's orientation, including crane and release-knob turns. */
  supportRotationToRef(pose: PistolPose, out: Quaternion): void {
    this.supportRotationAtToRef(pose.supportFrom, this.supportRotationA);
    this.supportRotationAtToRef(pose.supportTo, this.supportRotationB);
    Quaternion.SlerpToRef(this.supportRotationA, this.supportRotationB, pose.supportBlend, out);
  }

  private supportRotationAtToRef(index: number, out: Quaternion): void {
    const marker = this.supportMarkers[index]!;
    marker.computeWorldMatrix(true);
    marker.absoluteRotationQuaternion.multiplyToRef(PISTOL_RELOAD_CONTACTS[index]!.rotation, out);
  }

  private supportAnchorToRef(index: number, out: Vector3): void {
    const stage = PISTOL_RELOAD_CONTACTS[index]!;
    this.supportMarkers[index]!.computeWorldMatrix(true).getTranslationToRef(out);
    Matrix.FromQuaternionToRef(stage.rotation, this.supportMatrix);
    Vector3.TransformNormalToRef(stage.offset, this.supportMatrix, this.supportOffset);
    Vector3.TransformNormalToRef(this.supportOffset, this.supportMarkers[index]!.getWorldMatrix(), this.supportOffset);
    out.subtractInPlace(this.supportOffset);
  }
}
